import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { clinicApi } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { APT_TICKETS_KEY } from '@/features/apt-counter/useAptTickets';
import { CONSULTATIONS_KEY } from '@/features/patients/useConsultations';
import type { DispensedLine, IssueEntry } from '@/types/api';

export type { DispensedLine };
import type { OutsideMedicine, PatientSex } from '@/types/patients';

/** A recorded visit as the person typing it up sees it. */
export interface Visit {
  id: string;
  patientId: string;
  visitDate: string;
  recordedAt: string;
  diagnosisNote: string | null;
  outsideMedicines: OutsideMedicine[];
  issued: DispensedLine[];
}

export interface RecordedVisitListItem {
  id: string;
  visitDate: string;
  recordedAt: string;
  patient: {
    id: string;
    mrn: string;
    fullName: string;
    sex: PatientSex;
    dateOfBirth: string | null;
  };
}

export interface RecordVisitPayload {
  /** Client-chosen, once per form — the server's idempotency key. */
  id: string;
  ticketId?: string;
  visitDate?: string;
  diagnosisNote?: string;
  outsideMedicines: OutsideMedicine[];
  issued: IssueEntry[];
}

export interface AmendVisitPayload {
  diagnosisNote?: string;
  outsideMedicines: OutsideMedicine[];
  issued: {
    add: IssueEntry[];
    update: { movementId: string; quantity: number }[];
    remove: string[];
  };
}

export const VISITS_KEY = ['visits'] as const;
const detailKey = (id: string | null | undefined) => [...VISITS_KEY, 'detail', id] as const;

/**
 * Everything a saved visit can have made stale: the visit lists, the queue (a
 * ticket becomes "recorded"), stock levels and the stock ledger, and the
 * doctor's view of the patient's visits.
 */
function useInvalidateAfterVisitWrite() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: VISITS_KEY });
    qc.invalidateQueries({ queryKey: APT_TICKETS_KEY });
    qc.invalidateQueries({ queryKey: ['inventory'] });
    qc.invalidateQueries({ queryKey: CONSULTATIONS_KEY });
  };
}

/** The visits dated today (the server's today), newest first. */
export function useRecordedVisits() {
  const { isAuthenticated, activeClinic } = useAuth();
  return useQuery({
    queryKey: [...VISITS_KEY, 'today', activeClinic?.id ?? null],
    queryFn: () => clinicApi.get<{ date: string; visits: RecordedVisitListItem[] }>('visits'),
    enabled: isAuthenticated && !!activeClinic,
    staleTime: 5_000,
  });
}

export function useVisit(id: string | null | undefined) {
  return useQuery({
    queryKey: detailKey(id),
    queryFn: () => clinicApi.get<Visit>(`visits/${id}`),
    enabled: !!id,
    // Always fresh when the editor opens: the dispensed lines and their
    // `editable` flags change whenever anyone issues from the same batch.
    staleTime: 0,
  });
}

export function useRecordVisit(patientId: string) {
  const invalidate = useInvalidateAfterVisitWrite();
  return useMutation({
    mutationFn: (payload: RecordVisitPayload) =>
      clinicApi.post<Visit>(`patients/${patientId}/visits`, payload),
    onSuccess: invalidate,
  });
}

export function useAmendVisit(id: string) {
  const qc = useQueryClient();
  const invalidate = useInvalidateAfterVisitWrite();
  return useMutation({
    mutationFn: (payload: AmendVisitPayload) => clinicApi.put<Visit>(`visits/${id}`, payload),
    onSuccess: (visit) => {
      qc.setQueryData(detailKey(id), visit);
      invalidate();
    },
  });
}
